import { GameState } from '../game/GameState';
import { Config } from '../core/Config';
import { Engine } from '../core/Engine';
import { LevelManager } from '../game/LevelManager';

/**
 * UI component for displaying game start, game over, and game win screens
 */
export class GameScreenUI {
  private gameState: GameState;
  private engine: Engine;
  private levelManager: LevelManager;
  
  // UI elements
  private overlayElement: HTMLElement;
  private startScreenElement: HTMLElement;
  private gameOverElement: HTMLElement;
  private gameWinElement: HTMLElement;
  
  constructor(engine: Engine) {
    this.gameState = GameState.getInstance();
    this.engine = engine;
    this.levelManager = LevelManager.getInstance();
    
    console.log('GameScreenUI: Initializing UI component');
    
    // Create UI elements
    this.overlayElement = this.createOverlayElement();
    this.startScreenElement = this.createStartScreenElement();
    this.gameOverElement = this.createGameOverElement();
    this.gameWinElement = this.createGameWinElement();
    
    // Add to DOM
    document.body.appendChild(this.overlayElement);
    document.body.appendChild(this.startScreenElement);
    document.body.appendChild(this.gameOverElement);
    document.body.appendChild(this.gameWinElement);
    
    // Register event listeners with GameState
    this.gameState.onGameOver(() => this.showGameOverScreen());
    this.gameState.onGameWin(() => this.showGameWinScreen());
    
    // Show start screen by default
    this.showStartScreen();

    // Ensure cursor is unlocked when the page initially loads
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
    
    // Add a listener to ensure cursor is unlocked when the page is loaded/reloaded
    window.addEventListener('load', () => {
      if (document.pointerLockElement) {
        document.exitPointerLock();
      }
      console.log('GameScreenUI: Ensuring cursor is unlocked on page load');
    });
    
    console.log('GameScreenUI: Elements created and added to DOM');
  }
  
  /**
   * Create the semi-transparent overlay
   */
  private createOverlayElement(): HTMLElement {
    const element = document.createElement('div');
    
    // Set ID for potential styling
    element.id = 'game-screen-overlay';
    
    // Style the container
    element.style.position = 'fixed';
    element.style.top = '0';
    element.style.left = '0';
    element.style.width = '100%';
    element.style.height = '100%';
    element.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    element.style.backdropFilter = 'blur(5px)';
    element.style.zIndex = '2000';
    element.style.display = 'none';
    
    return element;
  }
  
  /**
   * Create the start screen
   */
  private createStartScreenElement(): HTMLElement {
    const element = document.createElement('div');
    
    // Set ID for potential styling
    element.id = 'start-screen';
    
    // Style the container
    element.style.position = 'fixed';
    element.style.top = '50%';
    element.style.left = '50%';
    element.style.transform = 'translate(-50%, -50%)';
    element.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    element.style.color = 'white';
    element.style.padding = '30px';
    element.style.borderRadius = '10px';
    element.style.fontFamily = 'Arial, sans-serif';
    element.style.textAlign = 'center';
    element.style.zIndex = '2001';
    element.style.display = 'none';
    element.style.maxWidth = '500px';
    
    // Build the content
    const title = document.createElement('h1');
    title.textContent = 'Rogue Toaster';
    title.style.margin = '0 0 20px 0';
    title.style.color = '#FFD700'; // Gold color
    
    const instructions = document.createElement('p');
    instructions.innerHTML = 'Defend the last loaf from hordes of rogue toasters!<br><br>' +
      '🍞 Guard your 40 slice loaf in the center of the map<br>' +
      '💰 Earn gold by destroying toasters<br>' +
      '🔫 Place towers (1) or use gun (2) to defend<br>' +
      '❗ Each toaster that reaches the loaf takes 2 slices';
    instructions.style.lineHeight = '1.6';
    instructions.style.marginBottom = '30px';
    
    const startButton = document.createElement('button');
    startButton.textContent = 'Start Game';
    startButton.style.padding = '12px 24px';
    startButton.style.backgroundColor = '#4CAF50'; // Green
    startButton.style.color = 'white';
    startButton.style.border = 'none';
    startButton.style.borderRadius = '4px';
    startButton.style.fontSize = '18px';
    startButton.style.cursor = 'pointer';
    startButton.style.transition = 'background-color 0.3s';
    
    // Add hover effect
    startButton.onmouseover = () => {
      startButton.style.backgroundColor = '#3e8e41';
    };
    startButton.onmouseout = () => {
      startButton.style.backgroundColor = '#4CAF50';
    };
    
    // Add start game functionality
    startButton.onclick = () => {
      this.hideStartScreen();
      this.gameState.startGame();
      // Start the first level
      this.levelManager.startLevel(0);
      // Lock mouse pointer for FPS mode
      document.documentElement.requestPointerLock();
    };
    
    // Add elements to the container
    element.appendChild(title);
    element.appendChild(instructions);
    element.appendChild(startButton);
    
    return element;
  }
  
  /**
   * Create the game over screen
   */
  private createGameOverElement(): HTMLElement {
    const element = document.createElement('div');
    
    // Set ID for potential styling
    element.id = 'game-over-screen';
    
    // Style the container
    element.style.position = 'fixed';
    element.style.top = '50%';
    element.style.left = '50%';
    element.style.transform = 'translate(-50%, -50%)';
    element.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    element.style.color = 'white';
    element.style.padding = '30px';
    element.style.borderRadius = '10px';
    element.style.fontFamily = 'Arial, sans-serif';
    element.style.textAlign = 'center';
    element.style.zIndex = '2001';
    element.style.display = 'none';
    element.style.maxWidth = '500px';
    
    // Build the content
    const title = document.createElement('h1');
    title.textContent = 'Game Over';
    title.style.margin = '0 0 20px 0';
    title.style.color = '#FF4444'; // Red color
    
    const message = document.createElement('p');
    message.textContent = 'Oh no! Your loaf has been completely toasted.';
    message.style.marginBottom = '30px';
    message.style.fontSize = '18px';
    
    const restartButton = document.createElement('button');
    restartButton.textContent = 'Try Again';
    restartButton.style.padding = '12px 24px';
    restartButton.style.backgroundColor = '#4CAF50'; // Green
    restartButton.style.color = 'white';
    restartButton.style.border = 'none';
    restartButton.style.borderRadius = '4px';
    restartButton.style.fontSize = '18px';
    restartButton.style.cursor = 'pointer';
    restartButton.style.transition = 'background-color 0.3s';
    
    // Add hover effect
    restartButton.onmouseover = () => {
      restartButton.style.backgroundColor = '#3e8e41';
    };
    restartButton.onmouseout = () => {
      restartButton.style.backgroundColor = '#4CAF50';
    };
    
    // Add restart functionality
    restartButton.onclick = () => {
      this.hideGameOverScreen();
      this.resetGame();
      this.showStartScreen();
    };
    
    // Add elements to the container
    element.appendChild(title);
    element.appendChild(message);
    element.appendChild(restartButton);
    
    return element;
  }
  
  /**
   * Create the game win screen
   */
  private createGameWinElement(): HTMLElement {
    const element = document.createElement('div');
    
    // Set ID for potential styling
    element.id = 'game-win-screen';
    
    // Style the container
    element.style.position = 'fixed';
    element.style.top = '50%';
    element.style.left = '50%';
    element.style.transform = 'translate(-50%, -50%)';
    element.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    element.style.color = 'white';
    element.style.padding = '30px';
    element.style.borderRadius = '10px';
    element.style.fontFamily = 'Arial, sans-serif';
    element.style.textAlign = 'center';
    element.style.zIndex = '2001';
    element.style.display = 'none';
    element.style.maxWidth = '500px';
    
    // Build the content
    const title = document.createElement('h1');
    title.textContent = 'Victory!';
    title.style.margin = '0 0 20px 0';
    title.style.color = '#FFD700'; // Gold color
    
    const message = document.createElement('p');
    message.textContent = 'Congratulations! You successfully defended your toast from all toaster attacks.';
    message.style.marginBottom = '30px';
    message.style.fontSize = '18px';
    
    const restartButton = document.createElement('button');
    restartButton.textContent = 'Play Again';
    restartButton.style.padding = '12px 24px';
    restartButton.style.backgroundColor = '#4CAF50'; // Green
    restartButton.style.color = 'white';
    restartButton.style.border = 'none';
    restartButton.style.borderRadius = '4px';
    restartButton.style.fontSize = '18px';
    restartButton.style.cursor = 'pointer';
    restartButton.style.transition = 'background-color 0.3s';
    
    // Add hover effect
    restartButton.onmouseover = () => {
      restartButton.style.backgroundColor = '#3e8e41';
    };
    restartButton.onmouseout = () => {
      restartButton.style.backgroundColor = '#4CAF50';
    };
    
    // Add restart functionality
    restartButton.onclick = () => {
      this.hideGameWinScreen();
      this.resetGame();
      this.showStartScreen();
    };
    
    // Add elements to the container
    element.appendChild(title);
    element.appendChild(message);
    element.appendChild(restartButton);
    
    return element;
  }
  
  /**
   * Show the start screen
   */
  private showStartScreen(): void {
    this.overlayElement.style.display = 'block';
    this.startScreenElement.style.display = 'block';
    
    // Exit pointer lock if active
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
  }
  
  /**
   * Hide the start screen
   */
  private hideStartScreen(): void {
    this.overlayElement.style.display = 'none';
    this.startScreenElement.style.display = 'none';
  }
  
  /**
   * Show the game over screen
   */
  private showGameOverScreen(): void {
    this.overlayElement.style.display = 'block';
    this.gameOverElement.style.display = 'block';
    
    // Ensure pointer lock is released for better button interaction
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
    
    console.log('GameScreenUI: Game over screen displayed, cursor unlocked');
  }
  
  /**
   * Hide the game over screen
   */
  private hideGameOverScreen(): void {
    this.overlayElement.style.display = 'none';
    this.gameOverElement.style.display = 'none';
  }
  
  /**
   * Show the game win screen
   */
  private showGameWinScreen(): void {
    this.overlayElement.style.display = 'block';
    this.gameWinElement.style.display = 'block';
    
    // Exit pointer lock if active
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
  }
  
  /**
   * Hide the game win screen
   */
  private hideGameWinScreen(): void {
    this.overlayElement.style.display = 'none';
    this.gameWinElement.style.display = 'none';
  }
  
  /**
   * Reset the game
   */
  private resetGame(): void {
    // Reload the site for a fresh start
    window.location.reload();
  }
} 